import {
  IRevenueSchedulePeriod,
  REV_REC_METHOD_DISPLAY,
} from '@monetize/types/app';
import { toDateShort } from '@monetize/utils/core';
import { PRODUCT_TYPE_DISPLAY } from '../constants/products';

const EM_DASH = '—';

/**
 * Display label for a product type as surfaced on revenue schedule rows.
 * Falls back to the raw value for unmapped types, and the em-dash placeholder
 * when absent.
 */
export function formatProductType(productType?: string | null): string {
  if (!productType) {
    return EM_DASH;
  }
  return (
    (PRODUCT_TYPE_DISPLAY as Record<string, string>)[productType] ?? productType
  );
}

/**
 * Display label for a revenue recognition method, optionally including its
 * duration in days when populated — e.g. `"Immediate (30 days)"`.
 *
 * - Unknown method keys fall back to the raw key.
 * - A missing method returns the em-dash placeholder.
 * - The duration suffix is only appended when `duration` is a number.
 */
export function formatRecognitionMethod(
  method?: string | null,
  duration?: number | null,
): string {
  if (!method) {
    return EM_DASH;
  }
  const label = REV_REC_METHOD_DISPLAY[method] ?? method;
  if (duration == null) {
    return label;
  }
  const unit = Math.abs(duration) === 1 ? 'day' : 'days';
  return `${label} (${duration} ${unit})`;
}

/**
 * Whether a (possibly in-progress) numeric input string stays within the given
 * number of decimal places. Used to block manual revenue overrides from
 * exceeding a currency's minor-unit precision (e.g. 2 dp for USD). A value with
 * no decimal point always passes.
 */
export function withinDecimalPrecision(
  value: string,
  precision: number,
): boolean {
  const dotIndex = value.indexOf('.');
  if (dotIndex === -1) {
    return true;
  }
  return value.length - dotIndex - 1 <= precision;
}

/**
 * Whether a period has already been recognized as of the given date — i.e. its
 * end date is strictly before `asOfIso`. Recognized periods are locked from
 * manual override.
 *
 * NOTE: this approximates "recognized" with "the period has fully elapsed",
 * which is all the schedule payload exposes today. Swap in a backend
 * recognized-as-of / locked flag here once one is available. Dates are ISO
 * `yyyy-MM-dd` strings (lexicographically comparable).
 */
export function isPeriodRecognized(
  periodEndInclusive: string,
  asOfIso: string,
): boolean {
  if (!periodEndInclusive || !asOfIso) {
    return false;
  }
  return periodEndInclusive < asOfIso;
}

export interface ServicePeriodRange {
  start: string;
  end: string;
}

/**
 * The key that groups a period into a table row. Shared by the service-period
 * index here and the table's row builders so the two stay aligned — falls back
 * to the period id when a period isn't tied to a subscription item.
 */
export const getPeriodGroupKey = (period: IRevenueSchedulePeriod) =>
  period.references.subscriptionItemId || period.id;

/**
 * Build a map of row group key → service period range (earliest period start →
 * latest period end) across the supplied periods. Used to surface a per-row
 * "Service Period Start – End" derived from the schedule's period data.
 */
export function buildServicePeriodIndex(
  periods: IRevenueSchedulePeriod[],
): Map<string, ServicePeriodRange> {
  const index = new Map<string, ServicePeriodRange>();
  for (const period of periods) {
    const key = getPeriodGroupKey(period);
    const { start, endInclusive } = period.period;
    const existing = index.get(key);
    if (!existing) {
      index.set(key, { start, end: endInclusive });
      continue;
    }
    if (start < existing.start) {
      existing.start = start;
    }
    if (endInclusive > existing.end) {
      existing.end = endInclusive;
    }
  }
  return index;
}

/**
 * Format a service period range as `"<start> – <end>"` using the short date
 * format. Returns the em-dash placeholder when no range is available.
 */
export function formatServicePeriod(range?: ServicePeriodRange | null): string {
  if (!range || (!range.start && !range.end)) {
    return EM_DASH;
  }
  const start = range.start ? toDateShort(range.start) : EM_DASH;
  const end = range.end ? toDateShort(range.end) : EM_DASH;
  return `${start} – ${end}`;
}
